/**
 * Armed-status strip — the pure state derivation behind the little HUD row that
 * summarises which safety/nav modes are currently ON, so nothing is ever
 * silently armed or silently off.
 *
 * This module is deliberately React-free and store-free: it takes a plain
 * snapshot of the relevant state and returns the ordered list of chips to draw.
 * The component (ArmedStrip.tsx) reads the stores with stable selectors and
 * hands the values in here, so the logic that decides "which chips show" is a
 * plain function we can unit-test without a renderer.
 *
 * The strip stays quiet until something is actually active: with nothing armed
 * the derivation returns an empty list and the component draws nothing.
 */
import type { AlarmsConfig } from '@/store/alarms';

/** Which mode a chip stands for. Doubles as the tap target the component maps. */
export type ArmedChipId = 'mob' | 'anchor' | 'alarms' | 'follow';

/**
 * How loud a chip should read. 'danger' is the red, drop-everything state (MOB,
 * a dragging anchor); 'warn' is an amber caution (armed alarms); 'active' is a
 * calm "this is on" (route follow, anchor set and holding).
 */
export type ArmedChipTone = 'danger' | 'warn' | 'active';

export interface ArmedChip {
  id: ArmedChipId;
  /** Small leading glyph for the pill. */
  icon: string;
  /** The short, glanceable label on the pill. */
  label: string;
  tone: ArmedChipTone;
  /** Fuller text for the title / aria-label. */
  title: string;
}

/** The snapshot the strip is derived from — plain values, no store objects. */
export interface ArmedStripInput {
  /** A live man-overboard is running. */
  mob: { active: boolean };
  /** Anchor watch: the hook is down (armed), and whether it's dragging now. */
  anchor: { armed: boolean; dragging: boolean };
  /** Following a route: whether we're following, and the route's name if known. */
  routeFollow: { following: boolean; routeName: string | null };
  /** How many underway alarm guards are armed (see countArmedAlarms). */
  alarms: { armedCount: number };
}

/**
 * The underway alarm guards we treat as "armed" for the count. These all default
 * OFF, so the count is 0 at a fresh boot and the chip stays away until the
 * skipper actually arms a guard — which is what keeps the strip quiet until
 * something's on.
 *
 * The two clearing master-switches (clearingEnabled / clearingDepthEnabled) are
 * deliberately NOT counted: they default ON and only do anything once a clearing
 * bearing or clearing depth has actually been drawn, so counting them would show
 * a permanent "Alarms 2" from first launch and defeat the point of the strip.
 */
const COUNTED_ALARM_FLAGS: (keyof AlarmsConfig)[] = [
  'proximityEnabled',
  'depthEnabled',
  'groundingEnabled',
  'tideHeightEnabled',
  'aisProximityEnabled',
  'aisCpaEnabled',
  'gnssAccuracyEnabled',
  'gnssLostEnabled',
  'gnssJumpEnabled',
  'lowBatteryEnabled',
];

/** Count the armed underway alarm guards from the alarms config. */
export function countArmedAlarms(config: AlarmsConfig): number {
  let n = 0;
  for (const flag of COUNTED_ALARM_FLAGS) {
    if (config[flag] === true) n += 1;
  }
  return n;
}

/**
 * Derive the chips to show, in priority order (most urgent first): a live MOB,
 * then the anchor (dragging leads), then armed alarms, then route follow. Only ON
 * things produce a chip, so an all-quiet input yields an empty list.
 */
export function deriveArmedChips(input: ArmedStripInput): ArmedChip[] {
  const chips: ArmedChip[] = [];

  if (input.mob.active) {
    chips.push({
      id: 'mob',
      icon: '\u{1F6DF}',
      label: 'MOB',
      tone: 'danger',
      title: 'Man overboard active — tap for the search sheet',
    });
  }

  if (input.anchor.armed) {
    chips.push(
      input.anchor.dragging
        ? {
            id: 'anchor',
            icon: '⚓',
            label: 'Dragging',
            tone: 'danger',
            title: 'Anchor watch: DRAGGING — tap for the anchor watch',
          }
        : {
            id: 'anchor',
            icon: '⚓',
            label: 'Anchor',
            tone: 'active',
            title: 'Anchor watch armed — tap for the anchor watch',
          },
    );
  }

  if (input.alarms.armedCount > 0) {
    chips.push({
      id: 'alarms',
      icon: '\u{1F4E2}',
      label: `Alarms ${input.alarms.armedCount}`,
      tone: 'warn',
      title: `${input.alarms.armedCount} alarm${input.alarms.armedCount === 1 ? '' : 's'} armed — tap for the alarms sheet`,
    });
  }

  if (input.routeFollow.following) {
    const name = input.routeFollow.routeName?.trim();
    chips.push({
      id: 'follow',
      icon: '➤',
      label: name ? `Following ${name}` : 'Following',
      tone: 'active',
      title: name ? `Following route ${name} — tap for the route` : 'Following a route — tap for the route',
    });
  }

  // Track recording deliberately has NO chip. It's a log, not an armed guard: it
  // changes nothing about how the boat is being navigated, and it was taking up
  // room on the HUD next to the things that do. Whether it's running is shown
  // where it's controlled, in the saved-tracks sheet.

  return chips;
}
